/**
 * NeIO Core behind this app's sign-in: a run's traces and a tag's place on the knowledge graph.
 *
 * `GET /neio/status` decides whether any NeIO link is shown at all. With NeIO off, every other
 * NeIO route answers 404 "NeIO Core is switched off on this environment"; a page opened directly
 * says so with the standard switched-off note.
 */
import type { Schemas } from "@neio/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiError, apiGet } from "./http";

export type NeioStatus = Schemas["NeioStatusView"];
export type TraceSummary = Schemas["TraceSummary"];
export type TraceList = Schemas["TraceListView"];

export type NodeKind = "routing" | "agent" | "tool" | "retrieval" | "llm" | "validator" | "gate";
export type NodeStatus = "ok" | "failed" | "skipped" | "blocked";

export interface Citation {
  document_id?: string | null;
  document_title?: string | null;
  passage?: string | null;
  locator?: string | null;
  score?: number | null;
}

export interface TraceNode {
  node_id: string;
  parent_id?: string | null;
  kind: NodeKind | string;
  name: string;
  status: NodeStatus | string;
  duration_ms?: number | null;
  rationale?: string | null;
  inputs?: Record<string, unknown> | null;
  outputs?: Record<string, unknown> | null;
  citations?: Citation[] | null;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
  total_tokens?: number | null;
  error?: string | null;
}

export interface TraceMeta {
  run_id?: string;
  job_id?: string;
  tag?: string;
  package?: string;
  kind?: string;
  http_request_id?: string;
  outcome?: string;
  [key: string]: unknown;
}

export interface Trace {
  request_id: string;
  workspace_id?: string | null;
  actor?: string | null;
  duration_ms?: number | null;
  total_tokens?: number | null;
  node_count?: number | null;
  question?: string | null;
  meta?: TraceMeta | null;
  nodes?: TraceNode[] | null;
}

export interface AuditCheck {
  request_id?: string;
  found?: boolean;
  /** The hash chain over the trace's audit entries checks out. */
  verified?: boolean | null;
  /** An older name for `verified`. */
  chain_valid?: boolean | null;
  entries?: number;
  [key: string]: unknown;
}

export interface TraceDetail { trace: Trace; audit?: AuditCheck | null }

export const NEIO_STATUS_KEY = ["neio", "status"] as const;

/** Whether NeIO runs here. `enabled` is true only once the server has said so. */
export function useNeioStatus() {
  const query = useQuery({
    queryKey: NEIO_STATUS_KEY,
    queryFn: () => apiGet("/api/v1/neio/status"),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return {
    status: query.data,
    enabled: query.data?.enabled === true,
    loading: query.isPending,
    error: query.error,
    refetch: query.refetch,
  };
}

export function fetchTrace(requestId: string): Promise<TraceDetail> {
  return apiGet("/api/v1/neio/traces/{request_id}", { path: { request_id: requestId } }) as unknown as Promise<TraceDetail>;
}

const OFF = /switched off/i;

/**
 * Why NeIO cannot be used here, or null. From the status (`enabled: false`) or from a 404 whose
 * reason says NeIO is switched off. A 404 for a trace Core does not hold is "not found", not off.
 */
export function neioOff(status: NeioStatus | undefined, error: unknown): string | null {
  if (status && status.enabled === false) return "NeIO Core is switched off on this environment.";
  if (error instanceof ApiError && error.status === 404 && OFF.test(error.message)) return error.message;
  return null;
}

/** A trace node and the nodes under it. */
export interface TreeNode { node: TraceNode; children: TreeNode[] }

/**
 * The nodes as a tree by `parent_id`, in the order Core recorded them. A node whose parent is not
 * in the trace is shown at the top level rather than lost.
 */
export function buildTree(nodes: TraceNode[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const node of nodes) byId.set(node.node_id, { node, children: [] });
  const roots: TreeNode[] = [];
  for (const node of nodes) {
    const item = byId.get(node.node_id)!;
    const parent = node.parent_id && node.parent_id !== node.node_id ? byId.get(node.parent_id) : undefined;
    if (parent) parent.children.push(item);
    else roots.push(item);
  }
  return roots;
}

/** The pill tone for a node's status. Status is always said in words too. */
export function statusTone(status: string): "ok" | "fail" | "review" | "info" {
  if (status === "ok") return "ok";
  if (status === "failed") return "fail";
  if (status === "blocked") return "review";
  return "info";
}

/** What the audit check says, in words, with its tone. */
export function auditBadge(audit: AuditCheck | null | undefined): { tone: "ok" | "fail" | "info"; text: string } {
  if (!audit || audit.found === false) return { tone: "info", text: "No audit record" };
  const verified = audit.verified ?? audit.chain_valid;
  if (verified === true) return { tone: "ok", text: "Audit chain verified" };
  if (verified === false) return { tone: "fail", text: "Audit chain does not verify" };
  return { tone: "info", text: "Audit chain not checked" };
}

/** `1.2 s`, `340 ms`, `2 min 5 s`. */
export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1).replace(/\.0$/, "")} s`;
  return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}

export function tokens(n: number | null | undefined): string {
  return n ? `${n.toLocaleString("en-US")} tokens` : "";
}

const PACKAGE_TAG = /^package run [^,]+, [^,]+, (.+)$/;

/**
 * A trace's name in a list. A package run records one trace per tag (question
 * "package run <id>, <package>, <tag>") and one for routing (`<run id>.run.<segment>`), one of
 * each per start or resume.
 */
export function traceLabel(t: Pick<TraceSummary, "request_id" | "question">): string {
  const parts = t.request_id.split(".");
  const tag = t.question ? PACKAGE_TAG.exec(t.question)?.[1] : undefined;
  if (tag) return `Tag ${tag}`;
  if (parts[0] !== "uc1" && parts[1] === "run") return "Run routing and decisions";
  return t.question || t.request_id;
}

/** The trace's route in this app. */
export function tracePath(requestId: string): string {
  return `/traces/${encodeURIComponent(requestId)}`;
}

// -- the knowledge graph ------------------------------------------------------------------------

export type TagGraph = Schemas["TagGraphView"];

export interface GraphEdge { predicate: string; target?: string; value?: string; source?: string; datatype?: string }
export interface GraphDocument { document: string; via?: string | null }
export interface GraphRecord {
  record?: string;
  connector?: string | null;
  locator?: string | null;
  kind?: string | null;
  label?: string | null;
  attributed_to?: string | null;
  generated_by?: string | null;
  started_at?: string | null;
  attributes?: Record<string, unknown>;
}

/** An entity's wire id (`model:ABB:266HSH`) as its kind and decoded parts, or null for an IRI. */
export function entityParts(id: string | null | undefined): { kind: string; parts: string[] } | null {
  if (!id || /^https?:/.test(id)) return null;
  const [kind, ...rest] = id.split(":");
  if (!kind || rest.length === 0) return null;
  const parts = rest.map((p) => { try { return decodeURIComponent(p); } catch { return p; } });
  return { kind, parts };
}

/** An entity in words: `Model 266HSH (ABB)`, `Page 4`. Anything else as it came. */
export function entityText(id: string | null | undefined): string {
  const e = entityParts(id);
  if (!e) return id ?? "";
  const [a, b, c] = e.parts;
  switch (e.kind) {
    case "manufacturer": return `Manufacturer ${a}`;
    case "model": return `Model ${b} (${a})`;
    case "package": return `Package ${a}`;
    case "status": return `Status ${a}`;
    case "tag": return `Tag ${b}`;
    case "document": return `Document ${b?.slice(0, 12) ?? ""}`;
    case "page": return `Page ${c}`;
    case "finding": return `Finding from ${c}`;
    case "pid": return `P&ID drawing ${b}`;
    default: return e.parts.join(" ");
  }
}

/** `obs:mentionedOn` as "mentioned on". */
export function predicateText(p: string): string {
  const local = p.includes(":") && !/^https?:/.test(p) ? p.slice(p.indexOf(":") + 1) : p.split(/[#/]/).pop() ?? p;
  return local.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
}

/** Where each source connector's record came from, in words. */
export const CONNECTOR_WORDS: Record<string, string> = {
  "master-index": "Master document index",
  "content-sweep": "Content sweep",
  "table-read": "Table read",
  "pid-reading": "P&ID reading",
  lifecycle: "Lifecycle check",
};

/** What kind of record a source wrote, in words. */
export const RECORD_KIND_WORDS: Record<string, string> = {
  "page-mention": "Mentioned on a page",
  "identity-read": "Identity read",
  finding: "Finding",
  "tag-named": "Tag named",
  "pid-tag": "Tag on a P&ID",
};

/** The neighborhood split the way a person reads it. */
export function graphSections(g: TagGraph) {
  const outgoing = (g.outgoing ?? []) as unknown as GraphEdge[];
  const incoming = (g.incoming ?? []) as unknown as GraphEdge[];
  const kindOf = (id?: string) => entityParts(id)?.kind;
  const linked = [
    ...outgoing.filter((e) => e.target).map((e) => ({ predicate: e.predicate, entity: e.target! })),
    ...incoming.filter((e) => e.source).map((e) => ({ predicate: e.predicate, entity: e.source! })),
  ];
  return {
    facts: outgoing.filter((e) => e.value !== undefined),
    identity: linked.filter((l) => ["manufacturer", "model", "status", "package"].includes(kindOf(l.entity) ?? "")),
    findings: linked.filter((l) => kindOf(l.entity) === "finding"),
    drawings: linked.filter((l) => kindOf(l.entity) === "pid"),
    other: linked.filter((l) => !["manufacturer", "model", "status", "package", "finding", "pid", "document", "page"]
      .includes(kindOf(l.entity) ?? "")),
    documents: (g.documents ?? []) as unknown as GraphDocument[],
    provenance: (g.provenance ?? []) as unknown as GraphRecord[],
  };
}
